import React from 'react';
import { Link } from 'react-router-dom';
import ImplantCenterLayout from './ImplantCenterLayout';

const DentalImplantsPage = () => {
  const pageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalProcedure",
        "name": "Dental Implant Surgery",
        "procedureType": "Surgical",
        "description": "Dental implants are permanent tooth replacements using bio-compatible titanium posts that fuse with the jawbone through osseointegration.",
        "howPerformed": "A titanium post is surgically implanted into the jawbone, allowed to heal and fuse with the bone (3-6 months), then topped with an abutment and custom crown.",
        "preparation": "Comprehensive dental exam, 360-degree panoramic X-rays, and evaluation of jawbone density",
        "followup": "Regular dental checkups and proper oral hygiene to ensure longevity"
      },
      {
        "@type": "Service",
        "name": "Dental Implants Fort Worth",
        "provider": {
          "@type": "Dentist",
          "name": "Fort Worth Dental Implant Center",
          "telephone": "+1-817-854-4329"
        },
        "areaServed": ["Fort Worth", "Dallas", "Arlington", "DFW"]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much do dental implants cost in Fort Worth?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Every patient's dental implant treatment is unique. Costs depend on factors like the number of implants, bone grafting needs, and the type of restoration. We don't publish prices because pricing only makes sense after a clinical exam — contact our Dental Implant Center for a personalized quote. Your FREE consultation includes X-rays and a written estimate."
            }
          },
          {
            "@type": "Question",
            "name": "Are dental implants painful?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Most patients report that dental implant surgery is less painful than expected. The procedure is performed under local anesthesia, and sedation options are available. Post-surgery discomfort is typically manageable with over-the-counter pain medication."
            }
          },
          {
            "@type": "Question",
            "name": "How long do dental implants last?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "With proper care and regular dental checkups, dental implants can last a lifetime. The titanium post fuses permanently with your jawbone. The crown on top may need replacement after 10-15 years due to normal wear."
            }
          },
          {
            "@type": "Question",
            "name": "Am I a good candidate for dental implants?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Most adults with good general health are candidates for dental implants. You need adequate jawbone density to support the implant, healthy gums, and commitment to good oral hygiene. Bone grafting can help those with bone loss. Schedule a free consultation to find out if implants are right for you."
            }
          },
          {
            "@type": "Question",
            "name": "What is the dental implant process?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The dental implant process typically involves: 1) Consultation and X-rays, 2) Implant placement surgery, 3) 3-6 months healing time for osseointegration, 4) Abutment placement, and 5) Custom crown attachment. Some patients may qualify for same-day implants."
            }
          }
        ]
      }
    ]
  };

  const benefits = [
    {
      title: 'Natural Appearance',
      description: 'Dental implants look and feel like your own natural teeth. The crown is custom-made to match your existing teeth perfectly.'
    },
    {
      title: 'Improved Speech',
      description: 'Unlike ill-fitting dentures that can slip and cause mumbling, dental implants allow you to speak confidently without worry.'
    },
    {
      title: 'Comfortable & Permanent',
      description: 'Because they fuse with your jawbone, implants become a permanent part of you—no more discomfort from removable dentures.'
    },
    {
      title: 'Easier Eating',
      description: 'Sliding dentures make chewing difficult. Dental implants function like your own teeth, allowing you to eat your favorite foods.'
    },
    {
      title: 'Improved Self-Esteem',
      description: 'Dental implants give you back your smile and help you feel better about yourself with renewed confidence.'
    },
    {
      title: 'Better Oral Health',
      description: 'Unlike bridges, implants don\'t require reducing adjacent teeth. More of your natural teeth remain intact.'
    },
    {
      title: 'Durability',
      description: 'Dental implants are incredibly durable and with proper care, can last a lifetime—making them a long-term investment.'
    },
    {
      title: 'Convenience',
      description: 'No more removing dentures, no messy adhesives. Dental implants eliminate the inconvenience of removable prosthetics.'
    }
  ];

  return (
    <ImplantCenterLayout
      title="Dental Implants Fort Worth TX | Permanent Tooth Replacement | Personalized Quote"
      description="Dental implants in Fort Worth TX — permanent tooth replacement with titanium implants. 35+ years experience. FREE consultation with X-rays + personalized quote. Same-day options available. Call (817) 854-4329."
      keywords="dental implants Fort Worth TX, dental implant cost, permanent tooth replacement, titanium dental implants, implant dentist Fort Worth, dental implant surgery Fort Worth, best dental implants near me, affordable dental implants Texas"
      schema={pageSchema}
    >
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{backgroundImage: "url('/images/services/dental-implant-model.jpg')"}} />
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Dental Implants in Fort Worth, TX
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              The modern standard of care for missing teeth. Permanent, natural-looking tooth replacement 
              with over 35 years of expertise. Contact us for a personalized quote at your FREE consultation.
            </p>
            <a href="tel:8178544329" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition inline-block">
              FREE Consultation: (817) 854-4329
            </a>
          </div>
        </div>
      </section>

      {/* What Are Dental Implants */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              What Are Dental Implants?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-4">
                <strong>Dental implants</strong> are the gold standard for replacing missing teeth. Unlike traditional dentures 
                or bridges, implants provide a permanent solution that looks, feels, and functions like your natural teeth.
              </p>
              <p className="mb-4">
                A dental implant consists of three components:
              </p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Titanium Post:</strong> A bio-compatible screw that's surgically placed into your jawbone, acting as an artificial tooth root.</li>
                <li><strong>Abutment:</strong> A connector piece that attaches to the titanium post and protrudes above the gum line.</li>
                <li><strong>Crown:</strong> A custom-made ceramic tooth that matches your natural teeth perfectly.</li>
              </ul>
              <p>
                Over time, the titanium post fuses with your jawbone through a natural process called <strong>osseointegration</strong>, 
                creating an incredibly stable foundation for your new tooth. This process typically takes 3-6 months.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Benefits of Dental Implants
            </h2>
            <p className="text-gray-600 text-lg">Why dental implants are the best choice for missing teeth</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-teal-600 font-bold text-xl">{index + 1}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* When Are Implants Right */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                When Are Dental Implants Right for You?
              </h2>
              <p className="text-gray-700 mb-4">
                If you're missing one or more teeth, dental implants may be the ideal solution. They're particularly 
                beneficial if you:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-teal-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Have difficulty chewing due to missing teeth or loose dentures</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-teal-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Feel self-conscious about gaps in your smile</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-teal-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Want a permanent solution rather than removable dentures</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-teal-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Have adequate jawbone density (or are willing to undergo grafting)</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-teal-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Are in good general health and don't smoke</span>
                </li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="text-yellow-800">
                  <strong>Note:</strong> Some conditions like uncontrolled diabetes, heavy smoking, or radiation therapy 
                  may affect candidacy. Our FREE consultation will determine if implants are right for you.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="/images/services/dental-implant-procedure.jpg" 
                alt="Dental implant procedure Fort Worth" 
                className="rounded-xl shadow-xl"
                onError={(e) => { e.target.src = '/images/services/dentist-examining.jpg'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Implant Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">
              The Dental Implant Process
            </h2>
            
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl">1</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">FREE Consultation & Planning</h3>
                  <p className="text-gray-700">
                    Your journey begins with a comprehensive exam and 360-degree panoramic X-rays. We evaluate your jawbone 
                    structure, oral health, and create a precise digital surgical guide using advanced 3D imaging technology.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl">2</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Implant Placement Surgery</h3>
                  <p className="text-gray-700">
                    Using the digital surgical guide, we precisely place the titanium post into your jawbone at the optimal 
                    angle for maximum support. Sedation is provided for your comfort throughout the procedure.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl">3</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Osseointegration (Healing)</h3>
                  <p className="text-gray-700">
                    Over 3-6 months, your jawbone naturally fuses with the titanium post through osseointegration. This creates 
                    an incredibly stable foundation that mimics a natural tooth root.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl">4</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Crown Placement</h3>
                  <p className="text-gray-700">
                    Once healed, we attach the abutment and place your custom-made ceramic crown. The crown is designed 
                    to perfectly match your natural teeth in color, shape, and size.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing — implant dollar amounts are NEVER shown publicly per
          practice policy; every case is unique and pricing only makes sense
          after a clinical exam. CTA + value props instead. */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Dental Implant Cost in Fort Worth
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-10">
              Every smile is unique — so is every treatment plan. Implant pricing depends on the number of teeth being replaced, whether bone grafting is needed, and the type of restoration that fits you best. Rather than publishing one-size-fits-all numbers that won't apply to your situation, we give you a precise, written quote at your free consultation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="text-3xl mb-3" aria-hidden="true">🦷</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">FREE Consultation</h3>
                <p className="text-gray-600 text-sm">3D scan, X-rays, and a written treatment plan — no obligation.</p>
              </div>
              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <div className="text-3xl mb-3" aria-hidden="true">💳</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">0% Financing</h3>
                <p className="text-gray-600 text-sm">Flexible monthly plans through CareCredit and Lending Club. Most patients qualify.</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="text-3xl mb-3" aria-hidden="true">🛡️</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Insurance Welcomed</h3>
                <p className="text-gray-600 text-sm">We accept most PPO insurance plans and file claims for you.</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <Link
                to="/contact-us"
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-bold"
                data-testid="implant-quote-cta"
              >
                Get My Personalized Quote
              </Link>
              <a
                href="tel:8179853337"
                className="bg-white border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold"
              >
                Call (817) 985-3337
              </a>
            </div>
            <Link to="/implant-center/fort-worth-dental-provides-a-dental-plan-you-can-afford" className="text-teal-600 hover:text-teal-700 font-semibold">
              Learn about our $199/year Dental Plan →
            </Link>
          </div>
        </div>
      </section>
    </ImplantCenterLayout>
  );
};

export default DentalImplantsPage;
